import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import type { BuddyView, KidBuddyRequestView } from '@kidzim/shared';
import { Avatar, Button, Card, Input, Logo, StatusDot } from '../design-system/components';
import { useAuth } from '../lib/auth';
import { api, type ApiError } from '../lib/api';

interface KidRoom {
  id: string;
  name: string;
}

export function Buddies() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const [buddies, setBuddies] = useState<BuddyView[]>([]);
  const [requests, setRequests] = useState<KidBuddyRequestView[]>([]);
  const [rooms, setRooms] = useState<KidRoom[]>([]);
  const [loading, setLoading] = useState(true);

  async function refresh() {
    const [b, r, m] = await Promise.all([
      api<{ buddies: BuddyView[] }>('/api/buddies'),
      api<{ requests: KidBuddyRequestView[] }>('/api/buddies/requests'),
      api<{ rooms: KidRoom[] }>('/api/rooms/mine').catch(() => ({ rooms: [] })),
    ]);
    setBuddies(b.buddies);
    setRequests(r.requests);
    setRooms(m.rooms);
    setLoading(false);
  }

  useEffect(() => {
    refresh().catch(() => setLoading(false));
  }, []);

  const online = buddies.filter((b) => b.online);
  const offline = buddies.filter((b) => !b.online);

  return (
    <div style={{ maxWidth: 640, margin: '0 auto', padding: 'var(--space-6) var(--space-4)' }}>
      <div className="kz-row" style={{ justifyContent: 'space-between', marginBottom: 'var(--space-6)' }}>
        <Logo height={30} />
        <Button
          variant="ghost"
          size="sm"
          onClick={async () => {
            await logout();
            nav('/');
          }}
        >
          Sign out
        </Button>
      </div>

      <h2 style={{ marginBottom: 'var(--space-1)' }}>Hi {user?.displayName} 👋</h2>
      <p className="kz-muted" style={{ marginBottom: 'var(--space-6)' }}>Here are your buddies and chat rooms.</p>

      {loading ? (
        <p className="kz-muted">Loading…</p>
      ) : (
        <div className="kz-stack" style={{ gap: 'var(--space-6)' }}>
          <section>
            <h3 style={{ marginBottom: 'var(--space-3)' }}>My buddies</h3>
            {buddies.length === 0 ? (
              <Card>
                <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
                  No buddies yet. Ask a grown-up for a friend's family code and screen name, then add them below. 🌟
                </p>
              </Card>
            ) : (
              <Card className="kz-stack" style={{ gap: 'var(--space-2)' }}>
                {[...online, ...offline].map((b) => (
                  <BuddyRow key={b.kidId} buddy={b} onRemoved={refresh} />
                ))}
              </Card>
            )}
          </section>

          <AddBuddy onAdded={refresh} />

          {requests.length > 0 && (
            <section>
              <h3 style={{ marginBottom: 'var(--space-3)' }}>Waiting for a grown-up</h3>
              <Card className="kz-stack" style={{ gap: 'var(--space-2)' }}>
                {requests.map((r) => (
                  <RequestRow key={r.id} request={r} onCancelled={refresh} />
                ))}
              </Card>
            </section>
          )}

          <section>
            <h3 style={{ marginBottom: 'var(--space-3)' }}>My chat rooms</h3>
            {rooms.length === 0 ? (
              <Card>
                <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
                  No rooms yet. Ask a grown-up for a room link to start chatting. 🎈
                </p>
              </Card>
            ) : (
              <Card className="kz-stack" style={{ gap: 'var(--space-1)' }}>
                {rooms.map((room) => (
                  <Link
                    key={room.id}
                    to={`/chat/${room.id}`}
                    className="kz-row"
                    style={{ justifyContent: 'space-between', padding: '10px 6px', textDecoration: 'none', color: 'var(--ink-700)', font: 'var(--type-body-strong)' }}
                  >
                    <span>💬 {room.name}</span>
                    <span aria-hidden>→</span>
                  </Link>
                ))}
              </Card>
            )}
          </section>
        </div>
      )}
    </div>
  );
}

function BuddyRow({ buddy, onRemoved }: { buddy: BuddyView; onRemoved: () => void }) {
  const nav = useNavigate();
  const [busy, setBusy] = useState(false);
  const [chatError, setChatError] = useState<string | null>(null);

  async function openChat() {
    setChatError(null);
    setBusy(true);
    try {
      const { roomId } = await api<{ roomId: string }>(`/api/dms/${buddy.kidId}/open`, { method: 'POST', body: '{}' });
      nav(`/chat/${roomId}`);
    } catch {
      setChatError("Couldn't open buddy chat right now.");
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="kz-stack" style={{ gap: 4, padding: '6px 0' }}>
      <div className="kz-row" style={{ justifyContent: 'space-between' }}>
        <div className="kz-row" style={{ gap: 'var(--space-3)' }}>
          <span style={{ position: 'relative', display: 'inline-flex' }}>
            <Avatar name={buddy.screenName} size={40} />
            {buddy.online && (
              <span style={{ position: 'absolute', right: -1, bottom: -1 }}>
                <StatusDot status="online" />
              </span>
            )}
          </span>
          <div>
            <p style={{ font: 'var(--type-body-strong)' }}>{buddy.screenName}</p>
            <p className="kz-muted" style={{ font: 'var(--type-small)' }}>{buddy.online ? 'Online now' : 'Offline'}</p>
          </div>
        </div>
        <div className="kz-row" style={{ gap: 'var(--space-2)' }}>
          {buddy.dmAvailable && (
            <Button size="sm" disabled={busy} onClick={openChat}>
              Chat
            </Button>
          )}
          <Button
            variant="ghost"
            size="sm"
            disabled={busy}
            onClick={async () => {
              if (!confirm(`Remove ${buddy.screenName} from your buddies?`)) return;
              setBusy(true);
              try {
                await api(`/api/buddies/${buddy.kidId}`, { method: 'DELETE' });
                onRemoved();
              } finally {
                setBusy(false);
              }
            }}
          >
            Remove
          </Button>
        </div>
      </div>
      {!buddy.dmAvailable && (
        <p className="kz-muted" style={{ font: 'var(--type-small)', paddingLeft: 52 }}>
          Ask a grown-up to turn on buddy chat.
        </p>
      )}
      {chatError && (
        <p className="kz-error-text" style={{ paddingLeft: 52 }}>{chatError}</p>
      )}
    </div>
  );
}

function RequestRow({ request, onCancelled }: { request: KidBuddyRequestView; onCancelled: () => void }) {
  const [busy, setBusy] = useState(false);
  return (
    <div className="kz-row" style={{ justifyContent: 'space-between', padding: '6px 0' }}>
      <div>
        <p style={{ font: 'var(--type-body-strong)' }}>{request.screenName}</p>
        <p className="kz-muted" style={{ font: 'var(--type-small)' }}>
          {request.status === 'pending_requester'
            ? 'Waiting for your grown-up to say yes'
            : "Waiting for your buddy's grown-up"}
        </p>
      </div>
      <Button
        variant="ghost"
        size="sm"
        disabled={busy}
        onClick={async () => {
          setBusy(true);
          try {
            await api(`/api/buddies/requests/${request.id}/cancel`, { method: 'POST', body: '{}' });
            onCancelled();
          } finally {
            setBusy(false);
          }
        }}
      >
        Cancel
      </Button>
    </div>
  );
}

function AddBuddy({ onAdded }: { onAdded: () => void }) {
  const [familyCode, setFamilyCode] = useState('');
  const [screenName, setScreenName] = useState('');
  const [msg, setMsg] = useState<{ tone: 'ok' | 'err'; text: string } | null>(null);
  const [busy, setBusy] = useState(false);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setMsg(null);
    setBusy(true);
    try {
      const { status } = await api<{ status: string }>('/api/buddies/requests', {
        method: 'POST',
        body: JSON.stringify({ familyCode: familyCode.toUpperCase(), screenName }),
      });
      setFamilyCode('');
      setScreenName('');
      setMsg({
        tone: 'ok',
        text: status === 'accepted' ? 'Added to your buddies! 🎉' : 'Sent! A grown-up will say yes. ✨',
      });
      onAdded();
    } catch (err) {
      setMsg({ tone: 'err', text: addBuddyError(err as ApiError) });
    } finally {
      setBusy(false);
    }
  }

  return (
    <section>
      <h3 style={{ marginBottom: 'var(--space-3)' }}>Add a buddy</h3>
      <Card>
        <form className="kz-stack" onSubmit={submit}>
          <Input
            label="Their family code"
            placeholder="SUNNY-FOX"
            value={familyCode}
            onChange={(e) => setFamilyCode(e.target.value.toUpperCase())}
            autoCapitalize="characters"
            required
          />
          <Input label="Their screen name" value={screenName} onChange={(e) => setScreenName(e.target.value)} required />
          {msg && (
            <p style={{ font: 'var(--type-small)', color: msg.tone === 'ok' ? 'var(--safe-700)' : 'var(--flag-500)' }}>{msg.text}</p>
          )}
          <Button type="submit" disabled={busy}>{busy ? 'One sec…' : 'Ask to add buddy'}</Button>
        </form>
      </Card>
    </section>
  );
}

function addBuddyError(e: ApiError): string {
  switch (e.error) {
    case 'buddy_not_found':
      return "Hmm, no buddy found with that code and name. Check them with your friend.";
    case 'already_buddies':
      return "You're already buddies! 🎉";
    case 'request_exists':
      return 'You already asked to add this buddy.';
    case 'cannot_add_self':
      return "That's you! Pick a friend instead. 😊";
    case 'roster_full':
      return 'Your buddy list is full.';
    case 'too_many_requests':
      return 'Too many requests waiting. Ask a grown-up to help.';
    default:
      return 'Could not add that buddy right now.';
  }
}
